import React, { useState } from 'react';
import SEO from '../SEO';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, Activity, BarChart2, CheckCircle, Check, X, ChevronDown, ChevronUp, Globe, MousePointer, Shield, TrendingUp, Zap, Target, Layout, Clock, Play, Server, Lock, Code , Star} from 'lucide-react';

interface LandingPageProps {
    onLogin?: () => void;
    onNavigate?: (page: string) => void;
}

const ZhOrganicWebsiteTraffic: React.FC<LandingPageProps> = ({ onLogin, onNavigate }) => {
    const [scrolled, setScrolled] = useState(false);

    // Schema.org Structured Data
    const schema = [
        {
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "自然网站流量生成器 (Organic Website Traffic Generator)",
            "description": "高级自然流量服务，用于提升SEO排名、并通过真实的住宅IP提高点击率。",
            "brand": { "@type": "Brand", "name": "Traffic Creator" }
        },
        {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://traffic-creator.com" },
                { "@type": "ListItem", "position": 2, "name": "自然网站流量", "item": "https://traffic-creator.com/zh/goumai-ziran-liuliang" }
            ]
        }
    ];

    const benefits = [
        {
            icon: <Search className="w-6 h-6 text-[#ff5a0a]" />,
            title: "提高自然点击率 (CTR)",
            desc: "我们的访客会搜索您的关键词并点击您的结果，向搜索算法显示出高度的相关性。"
        },
        {
            icon: <Clock className="w-6 h-6 text-[#ff5a0a]" />,
            title: "增加页面停留时间",
            desc: "访客会在您的页面停留数分钟，滚动查看内容并进行互动，可大幅降低跳出率。"
        },
        {
            icon: <Globe className="w-6 h-6 text-[#ff5a0a]" />,
            title: "主导本地 SEO 排名",
            desc: "定位来自特定城市或区域的流量，以便在本地搜索和地图组合中获得更高的排名。"
        },
        {
            icon: <Shield className="w-6 h-6 text-[#ff5a0a]" />,
            title: "100% 真实的人类行为",
            desc: "鼠标移动、滚动以及点击行为均高度随机，完美重现真人互动。"
        }
    ];

    const comparisons = [
        { feature: "流量来源", us: "优质真实住宅IP", them: "廉价的数据中心代理" },
        { feature: "浏览行为", us: "滚动、点击、阅读", them: "单纯的静态访问" },
        { feature: "Google Analytics", us: "100% 被识别为真人", them: "通常被过滤 / 标记为机器" },
        { feature: "对SEO的影响", us: "正向排名信号", them: "中性或负面影响" },
        { feature: "AdSense 安全度", us: "非常安全 (已验证)", them: "极高的封号风险" },
    ];

    return (
        <div className="min-h-screen bg-gray-50 text-gray-900 font-sans overflow-x-hidden">
            <SEO
                title="2026 年购买自然网站流量 - 真实住宅IP & 点击率提升"
                description="在2026年获取来自真实住宅IP的100%自然流量。我们先进的AI流量机器人会模拟人类行为（滚动、点击、阅读），从而提升您的SERP排名并改善参与度指标。"
                keywords="自然网站流量, 购买自然流量, seo流量, 提升排名, 真实网站访客, ai流量机器人, organic website traffic, buy organic traffic"
                schema={schema}
                type="product"
            />

            {/* Added Hreflang Tags for SEO Localization */}

            {/* Navigation */}
            <nav className="fixed w-full top-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-md border-b border-gray-100/50">
                <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-2 group cursor-pointer" onClick={() => window.scrollTo(0, 0)}>
                        <Link to="/" className="flex items-center gap-2">
                            <span className="text-2xl font-black text-[#ff5a0a] tracking-tight group-hover:scale-105 transition-transform">TRAFFIC</span>
                            <span className="hidden sm:inline-block text-xs font-bold bg-black text-white px-2 py-1 rounded-sm uppercase tracking-wide">Creator</span>
                        </Link>
                    </div>
                    <div className="flex items-center gap-8">
                        <div className="hidden md:flex gap-8 text-sm font-bold uppercase tracking-wide text-gray-500">
                            <a href="#how-it-works" className="hover:text-[#ff5a0a] transition-colors relative group">
                                工作原理
                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#ff5a0a] transition-all group-hover:w-full"></span>
                            </a>
                            <a href="#benefits" className="hover:text-[#ff5a0a] transition-colors relative group">
                                功能特色
                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#ff5a0a] transition-all group-hover:w-full"></span>
                            </a>
                            <Link to="/#pricing" className="hover:text-[#ff5a0a] transition-colors relative group">
                                价格套餐
                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#ff5a0a] transition-all group-hover:w-full"></span>
                            </Link>
                        </div>
                        <Link to="/login" className="bg-black text-white px-6 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#ff5a0a] transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 flex items-center rounded-sm">
                            登录后台
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="pt-32 pb-32 px-6 relative overflow-hidden bg-white">
                {/* Background Decor */}
                <div className="absolute top-0 right-0 w-[50%] h-[50%] bg-gradient-to-b from-orange-50/50 to-transparent pointer-events-none rounded-bl-[100px]"></div>
                <div className="absolute top-40 left-10 w-4 h-4 rounded-full bg-orange-200 animate-pulse"></div>
                <div className="absolute top-60 right-20 w-3 h-3 rounded-full bg-blue-200 animate-bounce delay-700"></div>

                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
                    <div className="animate-in fade-in slide-in-from-bottom-8 duration-700">
                        <div className="inline-flex items-center gap-2 bg-orange-50 text-[#ff5a0a] border border-orange-100 px-4 py-2 text-xs font-bold uppercase tracking-widest mb-8 rounded-full shadow-sm hover:shadow-md transition-shadow cursor-default">
                            <Zap size={14} className="fill-[#ff5a0a]" /> SEO 智能搜索引擎
                        </div>
                        <h1 className="text-5xl md:text-7xl font-black tracking-tight leading-[0.9] mb-8 text-gray-900">
                            主导 <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ff5a0a] to-orange-500">自然</span><br />搜索排名提升。
                        </h1>
                        <p className="text-xl text-gray-500 mb-10 max-w-lg leading-relaxed font-medium">
                            专为SEO专家量身定制的唯一自然网站流量生成器。通过提升点击率(CTR)、增加页面停留时间，向Google等搜索引擎传递出权威的正向信号。
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <Link to="/signup" className="group bg-[#ff5a0a] text-white px-8 py-4 text-sm font-bold uppercase tracking-wider hover:bg-black transition-all shadow-xl hover:shadow-2xl flex items-center justify-center gap-3 rounded-sm transform hover:-translate-y-1">
                                开启 SEO 引流
                                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                            </Link>
                            <a href="#demo" className="bg-white border border-gray-200 text-gray-900 px-8 py-4 text-sm font-bold uppercase tracking-wider hover:border-[#ff5a0a] hover:text-[#ff5a0a] transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2 rounded-sm group">
                                <Play size={16} className="group-hover:fill-[#ff5a0a]" /> 观看演示视频
                            </a>
                        </div>

                        <div className="mt-8 flex items-center gap-4 text-xs font-bold text-gray-400 uppercase tracking-widest">
                            <span>已有 15,000+ SEO机构正在使用</span>
                            <div className="flex -space-x-2">
                                <div className="w-6 h-6 rounded-full bg-gray-200 border-2 border-white"></div>
                                <div className="w-6 h-6 rounded-full bg-gray-300 border-2 border-white"></div>
                                <div className="w-6 h-6 rounded-full bg-gray-400 border-2 border-white"></div>
                            </div>
                        </div>
                    </div>

                    {/* Visual: Abstract SEO Graph */}
                    <div className="relative h-[550px] perspective-1000 group">
                        {/* Card Reflection */}
                        <div className="absolute inset-0 bg-gradient-to-tr from-gray-100 to-gray-50 transform rotate-6 scale-95 opacity-50 rounded-2xl border border-gray-100 transition-transform duration-700 group-hover:rotate-3 group-hover:scale-95"></div>

                        {/* Main Interface Card */}
                        <div className="absolute inset-0 bg-white border border-gray-200 shadow-2xl rounded-2xl p-6 overflow-hidden transform transition-transform duration-700 group-hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(0,0,0,0.1)]">
                            <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent z-10 pointer-events-none"></div>

                            {/* Fake Browser Header */}
                            <div className="border-b border-gray-100 pb-4 mb-6 flex gap-2">
                                <div className="w-3 h-3 rounded-full bg-red-400/80"></div>
                                <div className="w-3 h-3 rounded-full bg-yellow-400/80"></div>
                                <div className="w-3 h-3 rounded-full bg-green-400/80"></div>
                                <div className="ml-6 bg-gray-100 rounded-lg h-5 w-64 flex items-center px-2">
                                    <Lock size={10} className="text-gray-400 mr-2" />
                                    <div className="w-32 h-2 bg-gray-200 rounded-sm"></div>
                                </div>
                            </div>

                            {/* SERP Simulation */}
                            <div className="space-y-6">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className={`p-5 rounded-lg border transition-all duration-500 hover:shadow-lg cursor-default relative overflow-hidden ${i === 1 ? 'border-[#ff5a0a]/20 bg-orange-50/30' : 'border-gray-50 bg-white'}`}>

                                        {i === 1 && <div className="absolute top-0 left-0 w-1 h-full bg-[#ff5a0a]"></div>}

                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-500">
                                                {i === 1 ? 'TC' : 'Ad'}
                                            </div>
                                            <div className="flex-1">
                                                <div className="w-32 h-2 bg-gray-200 rounded-sm mb-1"></div>
                                                <div className="w-48 h-2 bg-gray-100 rounded-sm"></div>
                                            </div>
                                        </div>

                                        <div className={`w-3/4 h-5 rounded-sm mb-3 ${i === 1 ? 'bg-gradient-to-r from-[#ff5a0a] to-orange-400' : 'bg-blue-100'}`}></div>
                                        <div className="space-y-2">
                                            <div className="w-full h-2 bg-gray-100 rounded-sm"></div>
                                            <div className="w-5/6 h-2 bg-gray-100 rounded-sm"></div>
                                        </div>

                                        {i === 1 && (
                                            <div className="mt-4 flex items-center gap-2 text-[#ff5a0a] text-xs font-bold uppercase tracking-wider animate-pulse">
                                                <TrendingUp size={12} /> 第 1 名 • 点击率加速进行中
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Floating Stats Badge */}
                            <div className="absolute bottom-6 right-6 bg-gray-900/95 backdrop-blur-md text-white p-5 rounded-xl shadow-2xl z-20 border border-gray-800 animate-in fade-in slide-in-from-bottom-4 delay-300">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-[#ff5a0a] mb-2 flex items-center gap-2">
                                    <Activity size={12} /> 数据指标
                                </div>
                                <div className="text-4xl font-black mb-1">18.4%</div>
                                <div className="text-xs text-gray-400 font-medium">自然点击率 (7天)</div>
                                <div className="w-full bg-gray-800 h-1 mt-3 rounded-full overflow-hidden">
                                    <div className="w-[70%] h-full bg-[#ff5a0a]"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Benefits Grid */}
            <section id="benefits" className="py-32 px-6 bg-white relative">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-24">
                        <h2 className="text-4xl font-black uppercase tracking-tight mb-6">为何选择提升至第一名？</h2>
                        <div className="w-16 h-1.5 bg-[#ff5a0a] mx-auto mb-8 rounded-full"></div>
                        <p className="text-gray-500 max-w-2xl mx-auto text-lg leading-relaxed">
                            搜索引擎算法在不断进化，单靠外链早已不够。您需要强大的 <span className="text-gray-900 font-bold">用户行为信号 (User Signals)</span>。Traffic Creator 可完美生成理想的停留时间、滚动深度和互动指标。
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
                        {benefits.map((b, i) => (
                            <div key={i} className="flex gap-8 p-10 border border-gray-100 hover:border-[#ff5a0a]/30 bg-white hover:bg-orange-50/20 shadow-sm hover:shadow-xl transition-all duration-300 group rounded-2xl relative overflow-hidden">
                                <div className="absolute top-0 left-0 w-1 h-0 bg-[#ff5a0a] group-hover:h-full transition-all duration-300"></div>

                                <div className="shrink-0">
                                    <div className="bg-orange-50 w-16 h-16 flex items-center justify-center rounded-2xl group-hover:bg-[#ff5a0a] group-hover:rotate-6 transition-all duration-300 text-[#ff5a0a] group-hover:text-white shadow-sm">
                                        {b.icon}
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-[#ff5a0a] transition-colors">{b.title}</h3>
                                    <p className="text-gray-500 leading-relaxed font-medium">
                                        {b.desc}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Technical Detail / Dark Mode Section */}
            <section className="py-32 px-6 bg-[#f9fafb] border-t border-gray-200">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-20 items-center">
                        <div className="space-y-12">
                            <div className="pl-8 border-l-2 border-gray-200 hover:border-[#ff5a0a] transition-colors duration-300">
                                <h3 className="text-2xl font-black uppercase tracking-tight mb-4 flex items-center gap-3">
                                    <Target className="text-[#ff5a0a]" size={24} /> 精准关键词拉升
                                </h3>
                                <p className="text-gray-500 text-lg leading-relaxed font-medium">
                                    不仅仅是把流量发送到您的链接。我们的系统会模拟真实用户在Google上搜索您的特定关键词，找到您的结果并点击，这是最能够证明相关性的核心信号。
                                </p>
                            </div>
                            <div className="pl-8 border-l-2 border-gray-200 hover:border-[#ff5a0a] transition-colors duration-300">
                                <h3 className="text-2xl font-black uppercase tracking-tight mb-4 flex items-center gap-3">
                                    <Layout className="text-[#ff5a0a]" size={24} /> 复杂的网页访问路径
                                </h3>
                                <p className="text-gray-500 text-lg leading-relaxed font-medium">
                                    您可以定义多页面访问路径。例如：访客首先进入您的博客文章阅读60秒，然后点击至价格页面，最后访问甚至填上联系表单。
                                </p>
                            </div>
                        </div>

                        {/* Code Block Visual */}
                        <div className="bg-[#111] text-gray-300 p-2 rounded-xl shadow-2xl relative overflow-hidden group">
                            {/* Window Actions */}
                            <div className="flex items-center gap-2 p-4 border-b border-gray-800 bg-[#151515] rounded-t-lg">
                                <div className="flex gap-2">
                                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                </div>
                                <div className="text-xs font-mono text-gray-500 ml-4 flex items-center gap-2">
                                    <Code size={12} /> campaign_config.json
                                </div>
                            </div>

                            <div className="p-8 font-mono text-xs md:text-sm leading-relaxed overflow-x-auto">
                                <div className="opacity-50 mb-4 text-[10px] uppercase tracking-widest">// Traffic Configuration</div>
                                <div><span className="text-purple-400">const</span> <span className="text-blue-400">campaign</span> = {'{'}</div>
                                <div className="pl-4">
                                    <span className="text-gray-400">target:</span> <span className="text-green-400">"Top 3 Positions"</span>,
                                </div>
                                <div className="pl-4">
                                    <span className="text-gray-400">keywords:</span> [
                                </div>
                                <div className="pl-8 text-orange-300">"ranking boost services",</div>
                                <div className="pl-8 text-orange-300">"buy organic traffic",</div>
                                <div className="pl-8 text-orange-300">"seo ctr manipulation"</div>
                                <div className="pl-4">],</div>
                                <div className="pl-4">
                                    <span className="text-gray-400">behavior:</span> {'{'}
                                </div>
                                <div className="pl-8">
                                    <span className="text-blue-300">timeOnPage:</span> <span className="text-yellow-300">"185s"</span>,
                                </div>
                                <div className="pl-8">
                                    <span className="text-blue-300">scrollDepth:</span> <span className="text-yellow-300">"85%"</span>,
                                </div>
                                <div className="pl-8">
                                    <span className="text-blue-300">bounceRate:</span> <span className="text-yellow-300">"12%"</span>
                                </div>
                                <div className="pl-4">{'}'}</div>
                                <div>{'}'};</div>

                                <div className="mt-6 flex items-center gap-2 text-green-500 text-[10px] font-bold uppercase tracking-widest">
                                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div> compiler ready
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Premium Comparison */}
            <section className="py-32 px-6 bg-white">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-black uppercase tracking-tight mb-4">质量决定一切</h2>
                        <p className="text-gray-500">切勿因为采用廉价且低劣的机器流量风险导致域名权重降低甚至降权。</p>
                    </div>

                    <div className="bg-white shadow-[0_10px_60px_rgba(0,0,0,0.05)] rounded-2xl overflow-hidden border border-gray-100">
                        <div className="grid grid-cols-3 bg-gray-900 text-white p-6 text-xs font-bold uppercase tracking-widest">
                            <div className="pl-4">核心差异</div>
                            <div className="text-[#ff5a0a] flex items-center gap-2"><Zap size={14} className="fill-[#ff5a0a]" /> Traffic Creator</div>
                            <div className="text-gray-500">廉价同行</div>
                        </div>
                        <div className="divide-y divide-gray-50">
                            {comparisons.map((c, i) => (
                                <div key={i} className="grid grid-cols-3 p-6 items-center text-sm font-medium hover:bg-gray-50 transition-colors group">
                                    <div className="font-bold text-gray-900 flex items-center gap-3 pl-4">
                                        <div className="w-1.5 h-1.5 rounded-full bg-gray-300 group-hover:bg-[#ff5a0a] transition-colors"></div>
                                        {c.feature}
                                    </div>
                                    <div className="flex items-center gap-3 text-[#ff5a0a] font-bold bg-orange-50/50 -my-6 py-6 border-x border-orange-100/50">
                                        <CheckCircle size={18} className="ml-4" /> {c.us}
                                    </div>
                                    <div className="text-gray-400 flex items-center gap-3 pl-4">
                                        <X size={16} /> {c.them}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-24 px-6 bg-white border-t border-gray-100">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16 px-4">
                        <span className="text-[#ff5a0a] font-bold tracking-wider uppercase text-xs mb-4 block">Testimonials</span>
                        <h2 className="text-4xl font-black text-gray-900 mb-6">成功案例</h2>
                        <p className="text-xl text-gray-500 max-w-2xl mx-auto">
                            了解专业人士如何利用我们的自然流量主导搜索引擎结果。
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-all">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden">
                                    <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=chen_w`} alt="陈伟" width="56" height="56" loading="lazy" />
                                </div>
                                <div>
                                    <div className="font-bold text-gray-900">陈伟</div>
                                    <div className="text-xs text-[#ff5a0a] font-bold uppercase tracking-wider">SEO 团队主管</div>
                                </div>
                            </div>
                            <p className="text-gray-600 font-medium italic mb-6">"这个平台的CTR优化效果惊人。使用真实家庭IP的点击帮助我们的网站快速提升了排名，且没有任何风险。"</p>
                            <div className="flex gap-1 text-[#ff5a0a]">
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                            </div>
                        </div>
                        <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-all">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden">
                                    <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=lin_f`} alt="林芳" width="56" height="56" loading="lazy" />
                                </div>
                                <div>
                                    <div className="font-bold text-gray-900">林芳</div>
                                    <div className="text-xs text-[#ff5a0a] font-bold uppercase tracking-wider">数字营销顾问</div>
                                </div>
                            </div>
                            <p className="text-gray-600 font-medium italic mb-6">"流量在GA4中看起来非常完美。页面停留时间长，跳出率低，完美模拟了真实人类用户的浏览行为。"</p>
                            <div className="flex gap-1 text-[#ff5a0a]">
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                            </div>
                        </div>
                        <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-all">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden">
                                    <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=zhang_q`} alt="张强" width="56" height="56" loading="lazy" />
                                </div>
                                <div>
                                    <div className="font-bold text-gray-900">张强</div>
                                    <div className="text-xs text-[#ff5a0a] font-bold uppercase tracking-wider">跨境电商运营</div>
                                </div>
                            </div>
                            <p className="text-gray-600 font-medium italic mb-6">"本地SEO排名优化必备工具。通过定向模拟不同城市的搜索点击，我们的本地商家页面曝光率翻倍。"</p>
                            <div className="flex gap-1 text-[#ff5a0a]">
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-32 px-6 bg-[#ff5a0a] relative overflow-hidden">
                <div className="absolute inset-0 opacity-10"></div>

                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tight mb-8 text-white">立刻开启排名提升</h2>
                    <p className="text-xl text-white/90 mb-12 font-medium max-w-2xl mx-auto">
                        您的竞争对手也许已经在使用。通过世界上最先进的自然流量生成器，改变竞争格局并脱颖而出吧！
                    </p>
                    <div className="flex flex-col sm:flex-row gap-6 justify-center">
                        <Link to="/signup" className="bg-white text-black px-12 py-5 text-sm font-bold uppercase tracking-wider hover:bg-black hover:text-white transition-all shadow-2xl flex items-center justify-center gap-3 rounded-sm transform hover:-translate-y-1">
                            开始免费试用 <ArrowRight size={18} />
                        </Link>
                        <Link to="/#pricing" className="bg-transparent border-2 border-white text-white px-12 py-5 text-sm font-bold uppercase tracking-wider hover:bg-white hover:text-[#ff5a0a] transition-all rounded-sm flex items-center justify-center">
                            查看定价信息
                        </Link>
                    </div>
                    <div className="mt-12 text-white/60 text-xs font-bold uppercase tracking-widest">
                        试用免绑信用卡 • 100% 数据安全 • 随时取消订阅
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-black text-white py-20 px-6 border-t border-gray-900">
                <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-12">
                    <div className="col-span-2">
                        <div className="text-3xl font-black text-white tracking-tight mb-6">TRAFFIC CREATOR</div>
                        <p className="text-gray-500 max-w-sm mb-8">
                            SEO 流量生成的行业标杆。我们全方位保护代理商与站长利益，通过安全、卓越的流量体系极大改善搜索排名效果。
                        </p>
                        <div className="flex gap-4">
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center text-gray-500 hover:bg-[#ff5a0a] hover:text-white transition-colors cursor-pointer">
                                    <Globe size={16} />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div>
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-8">平台</h4>
                        <ul className="space-y-4 text-sm font-medium text-gray-300">
                            <li><Link to="/#workflow" className="hover:text-[#ff5a0a] transition-colors">平台原理</Link></li>
                            <li><Link to="/#pricing" className="hover:text-[#ff5a0a] transition-colors">产品价格</Link></li>
                            <li><Link to="/login" className="hover:text-[#ff5a0a] transition-colors">控制面板</Link></li>
                            <li><Link to="/dashboard/affiliate" className="hover:text-[#ff5a0a] transition-colors">联盟计划</Link></li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-8">关于我们</h4>
                        <ul className="space-y-4 text-sm font-medium text-gray-300">
                            <li><Link to="/about" className="hover:text-[#ff5a0a] transition-colors">关于公司</Link></li>
                            <li><Link to="/blog" className="hover:text-[#ff5a0a] transition-colors">SEO 博客</Link></li>
                            <li><Link to="/helpdesk" className="hover:text-[#ff5a0a] transition-colors">帮助中心</Link></li>
                            <li><Link to="/legal/privacy" className="hover:text-[#ff5a0a] transition-colors">隐私政策</Link></li>
                            <li><Link to="/legal/terms" className="hover:text-[#ff5a0a] transition-colors">服务条款</Link></li>
                            <li><Link to="/legal/impressum" className="hover:text-[#ff5a0a] transition-colors">网站声明</Link></li>
                        </ul>
                    </div>
                </div>
                <div className="max-w-7xl mx-auto mt-20 pt-8 border-t border-gray-900 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-gray-600">
                    <div>© {new Date().getFullYear()} EasyTrafficBot UG (haftungsbeschränkt) · Amtsgericht Wuppertal, HRB 30863</div>
                    <div className="flex gap-4 text-gray-500">
                        <Link to="/buy-website-traffic" className="hover:text-[#ff5a0a] transition-colors">购买流量</Link>
                        <Link to="/organic-website-traffic" className="text-[#ff5a0a]">自然访客</Link>
                        <Link to="/free-website-traffic" className="hover:text-[#ff5a0a] transition-colors">免费流量</Link>
                    </div>
                    <div className="flex gap-2 items-center">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        <span>系统运行正常</span>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default ZhOrganicWebsiteTraffic;
